export const TENANT_ID = 'geisinger';
export const APP_ID = 'geisinger-web';

// Narrower content cap for secondary/settings pages (Personal Info,
// Communication Preferences, etc.). Figma restricts these to 830px, vs the
// wider CONTENT_MAX_WIDTH (1272px) used by primary pages like Home. Shared so
// these pages stay width-consistent. Kept in px: the Genesis theme's root
// font-size is 10px (not the 16px Figma assumes), so Figma's 51.875rem
// resolves to 830px — writing it as rem would shrink the layout.
export const SECONDARY_CONTENT_MAX_WIDTH = '830px';

export const MASONRY = {
  ROOT_NODE_ID_HOME_PAGE: 'homePage$root',
  ROOT_NODE_ID_PLAN_SWITCHER: 'planSwitcher$root',
  // Standalone error root (GEIS-621), mounted for both the `no_plans` and
  // `error` bootstrap signals. Deliberately separate from the plan switcher
  // root: `error` means the plans extension already failed, so a node that
  // resolves plans would fail a second time and show a generic error instead
  // of the designed screen. Two segments — the tenant-prefixed form 404s.
  ROOT_NODE_ID_PLAN_SWITCHER_ERROR: 'planSwitcher$errorRoot',
  // Not under `planSwitcher$`: a plans-resolving root would fail for a member who
  // has no plan.
  ROOT_NODE_ID_INELIGIBLE_MEMBER: 'eligibility$ineligibleRoot',
  ROOT_NODE_ID_SUPPORT_PAGE: 'supportPage$root',
  ROOT_NODE_ID_PREMIUM_PAYMENTS: 'premiumPayments$root',
  ROOT_NODE_ID_FAQ: 'faq$root',
  // Health Alerts detail (HP-6). Two segments and no tenant prefix: the spec
  // reader strips the served tenant off the path, so the `geisinger$…` form the
  // GEIS-303/304/305/306 descriptions use 404s. The `homePage$` segment is part
  // of the id — the specs live under `nodes/geisinger/homePage/`.
  ROOT_NODE_ID_HEALTH_ALERTS: 'homePage$healthAlertsDetail$root',
  // Pharmacy benefits static page. Two segments and no tenant prefix, like the
  // roots above — the spec reader strips the served tenant off the path, so a
  // `geisinger$…` form would 404.
  ROOT_NODE_ID_PHARMACY: 'pharmacy$root',
  // Perks & Discounts static page. Two segments and no tenant prefix, like the
  // roots above. `perksAndDiscounts` is camelCase and is part of the id — the
  // specs live under `nodes/geisinger/perksAndDiscounts/`, so the kebab-case
  // form the route path uses (`/perks`) resolves nothing here.
  ROOT_NODE_ID_PERKS: 'perksAndDiscounts$root',
  // Wellness & Rewards benefits page (GEIS-307). Two segments, untenanted,
  // camelCase — same shape as the roots above; the route path is kebab-case but
  // the node id is not.
  ROOT_NODE_ID_WELLNESS_REWARDS: 'wellnessRewards$root',
  // Full "Suggested for you" list behind the Journey landing's View all
  // (GEIS-2499). Three segments: the specs live under
  // `nodes/geisinger/journey/suggested/`.
  ROOT_NODE_ID_SUGGESTED_ACTIVITIES: 'journey$suggested$root',
  // Prior Authorizations (GEIS-242). The SDK ships reference-tree defaults; the
  // Geisinger trees are published under these ids, so every route passes one
  // explicitly. Details and learn-more are kebab-case, matching the web routes.
  ROOT_NODE_ID_PRE_AUTHORIZATIONS: 'authorizations$rootWeb',
  ROOT_NODE_ID_PRE_AUTHORIZATIONS_DETAILS: 'pre-authorizations-details$rootWeb',
  // `$webRoot`, not `$rootWeb` — the two above really do use the opposite
  // segment order. Learn-more's web tree was added later (services#45618) under
  // `nodes/geisinger/pre-authorizations-learn-more/webRoot.yaml`, and the
  // served tree is matched on the literal id, so "correcting" this to `$rootWeb`
  // resolves nothing and 404s the node.
  ROOT_NODE_ID_PRE_AUTHORIZATIONS_LEARN_MORE:
    'pre-authorizations-learn-more$webRoot',
  // Emitted by the error root's "Try again" button and intercepted client-side
  // by usePlanSwitcherRetry. Not a route the app serves.
  DEEPLINK_PLAN_SWITCHER_RETRY: '/member/plan-switcher/retry',
  // Intercepted by useEligibilitySignOut. Not a route the app serves.
  DEEPLINK_ELIGIBILITY_SIGN_OUT: '/member/eligibility/sign-out',
  // Arrives relative (no leading slash) from Get Care; served by the League
  // REST host, not the app. Handled app-wide by appDeeplinkActionHandler.
  DEEPLINK_GEISINGER_SSO: '/sso/vendors/geisinger',
};

export const MESSAGE_TYPES = {
  LINK_EMAIL: 'link_email',
  ACCEPT_MEMBER_TERMS: 'accept_member_terms',
  RESOLVE_PLAN_SELECTION: 'resolve_plan_selection',
  // The web SDK never calls this, so the eligibility gate fetches it here.
  GET_APP_INIT_DETAILS: 'get_app_init_details',
  // Marks account-level onboarding (FTUX) complete server-side. The backend
  // merges the info keys into member_post_ftux and flips the top-level
  // ftux_complete flag once no step is outstanding; that flag is what the
  // returning-member gate reads (parity with iOS/Android).
  SET_POST_FTUX: 'set_post_ftux',
} as const;

// Version of the T&C document the member accepts. MUST stay a string —
// the backend's accept_member_terms Request.Version is a Go string and a
// JSON number is silently dropped (audit record would persist version "").
// '2' matches what geisinger-ios ships; exact canonical value pending
// PM/BE confirmation.
export const TERMS_AND_CONDITIONS_VERSION = '2';

export const MUTATION_KEYS = {
  UPDATE_USER_EMAIL: 'update_user_email',
} as const;

export const PHONE_NUMBER_TYPES = {
  MOBILE: 'mobile',
  PREFERRED: 'preferred',
} as const;

export const SENTRY_CONSTANTS = {
  OWNERSHIP_TEAM: 'pod-caresource-geisinger',
} as const;
